import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { Trophy, Sparkles } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { isUserPremium } from '../../utils/premium';
import CaddieProGate from '../../components/coaching/CaddieProGate';

const HandicapCoach = () => {
  const { currentUser } = useContext(AppContext);
  const [tips, setTips] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [games, setGames] = useState(null);

  useEffect(() => {
    if (!isUserPremium(currentUser)) return;
    const fetchGames = async () => {
      try {
        const completedGames = await firestoreService.getUserCompletedGames(currentUser.id);
        setGames(completedGames.slice(0, 20));
      } catch (err) {
        console.error('Error fetching games:', err);
        setGames([]);
      }
    };
    fetchGames();
  }, [currentUser.id, currentUser.isPremium, currentUser.trialStartDate, currentUser.trialUsed]);

  const getCoachingAdvice = async () => {
    if (!games || games.length < 3) return;
    setLoading(true);
    setError(null);
    try {
      const scores = games.map(game => {
        const holes = game.holes || {};
        const totalScore = Object.values(holes).reduce((sum, h) => sum + (h.scores?.[currentUser.id] || 0), 0);
        const totalPar = Object.values(holes).reduce((sum, h) => sum + (h.par || 4), 0);
        return {
          scoreVsPar: totalScore - totalPar,
          date: game.completedAt || game.startedAt,
          courseName: game.courseName || '',
        };
      }).filter(s => s.scoreVsPar !== 0 || true); // include all rounds

      const result = await firestoreService.getCoachingTips({
        handicap: currentUser.handicap || 0,
        skillLevel: currentUser.skillLevel || 'Beginner',
        scores,
      });
      setTips(result.tips);
    } catch (err) {
      console.error('Coaching tips error:', err);
      if (err.code === 'functions/permission-denied') {
        setError('Caddie Pro subscription required to use My Coach.');
      } else if (err.code === 'functions/unauthenticated') {
        setError('Please sign in again and retry.');
      } else {
        setError('Unable to get coaching tips right now. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isUserPremium(currentUser)) {
    return <CaddieProGate />;
  }

  if (games === null) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2"><Sparkles className="w-6 h-6 text-yellow-500" /><span>My Coach</span></h2>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="bg-gray-800 rounded-lg h-24" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
        <Sparkles className="w-6 h-6 text-yellow-500" />
        <span>My Coach</span>
      </h2>

      {/* Stats summary */}
      {games.length >= 3 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {(() => {
            const scoreDiffs = games.map(game => {
              const holes = game.holes || {};
              const totalScore = Object.values(holes).reduce((sum, h) => sum + (h.scores?.[currentUser.id] || 0), 0);
              const totalPar = Object.values(holes).reduce((sum, h) => sum + (h.par || 4), 0);
              return totalScore - totalPar;
            });
            const avg = (scoreDiffs.reduce((a, b) => a + b, 0) / scoreDiffs.length).toFixed(1);
            const best = Math.min(...scoreDiffs);
            const worst = Math.max(...scoreDiffs);
            return (
              <>
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400">Avg vs Par</p>
                  <p className="text-lg font-bold text-white">{avg >= 0 ? '+' : ''}{avg}</p>
                </div>
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400">Best Round</p>
                  <p className="text-lg font-bold text-[#16a34a]">{best >= 0 ? '+' : ''}{best}</p>
                </div>
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400">Worst Round</p>
                  <p className="text-lg font-bold text-red-400">{worst >= 0 ? '+' : ''}{worst}</p>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {games.length < 3 ? (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 text-center">
          <Trophy className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-white font-medium mb-1">Not enough rounds yet</p>
          <p className="text-gray-400 text-sm">Complete at least 3 rounds to get AI coaching tips.</p>
          <p className="text-gray-500 text-xs mt-2">You have {games.length} completed round{games.length !== 1 ? 's' : ''}.</p>
        </div>
      ) : !tips ? (
        <button
          onClick={getCoachingAdvice}
          disabled={loading}
          className="w-full bg-[#16a34a] text-white py-3 rounded-lg font-bold hover:bg-[#15803d] disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Analyzing your game...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              <span>Get AI Coaching Tips</span>
            </>
          )}
        </button>
      ) : null}

      {error && (
        <div className="rounded-lg p-4 mt-4" style={{ backgroundColor: 'rgb(var(--color-warning-red-bg) / 0.3)', borderWidth: '1px', borderColor: 'rgb(var(--color-warning-red-border))' }}>
          <p className="text-sm" style={{ color: 'rgb(var(--color-warning-red))' }}>{error}</p>
        </div>
      )}

      {tips && (
        <div className="space-y-4 mt-4">
          {tips.split(/TIP \d:/).filter(Boolean).map((tip, i) => {
            const parts = tip.trim().split(' — ');
            const title = parts[0]?.replace(/^:\s*/, '').trim();
            const body = parts.slice(1).join(' — ').trim();
            return (
              <div key={i} className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-[#16a34a] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white font-bold text-sm">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">{title}</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">{body}</p>
                  </div>
                </div>
              </div>
            );
          })}
          <button
            onClick={() => { setTips(null); getCoachingAdvice(); }}
            className="w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-700 text-sm"
          >
            Refresh Tips
          </button>
        </div>
      )}
    </div>
  );
};

export default HandicapCoach;
